import { Permission } from '../types/permissions';

// Constantes para las secciones de permisos
export const PERMISSION_SECTIONS = {
  ROLES_AND_PERMISSIONS: 1,
  ORDERS_AND_QUOTATIONS: 2,
  PRODUCTS: 3,
  OTHERS: 4, // Para permisos que no pertenecen a las secciones principales
  ENVIOS: 6,
  BODEGA: 7
} as const;

// Nombres de las secciones
export const SECTION_NAMES = {
  [PERMISSION_SECTIONS.ROLES_AND_PERMISSIONS]: 'Roles y Usuarios',
  [PERMISSION_SECTIONS.ORDERS_AND_QUOTATIONS]: 'Pedidos y Cotizaciones',
  [PERMISSION_SECTIONS.PRODUCTS]: 'Productos',
  [PERMISSION_SECTIONS.OTHERS]: 'Otros',
  [PERMISSION_SECTIONS.ENVIOS]: 'Envíos',
  [PERMISSION_SECTIONS.BODEGA]: 'Bodega'
} as const;

// Interfaz para permisos agrupados por sección
export interface PermissionSection {
  id: number;
  name: string;
  permissions: Permission[];
}

/**
 * Agrupa los permisos por secciones según la columna posicion
 */
export const groupPermissionsBySections = (permissions: Permission[]): PermissionSection[] => {
  // Crear un mapa para agrupar permisos por sección
  const sectionsMap = new Map<number, Permission[]>();
  
  permissions.forEach(permission => {
    // Agrupar posiciones 2 y 4 en "Pedidos y Cotizaciones" (sección 2)
    // Agrupar posición 5 en "Otros" (sección 4)
    // Posición 6: "Envíos", posición 7: "Bodega" (sin fusionar con otras secciones)
    const pos = Number(permission.posicion)
    let sectionId: number
    if (!Number.isFinite(pos)) {
      sectionId = PERMISSION_SECTIONS.OTHERS
    } else {
      sectionId = pos
      if (pos === 4) {
        sectionId = 2 // Agrupar con Pedidos y Cotizaciones
      } else if (pos === 5) {
        sectionId = 4 // Agrupar con Otros
      }
    }
    
    if (!sectionsMap.has(sectionId)) {
      sectionsMap.set(sectionId, []);
    }
    sectionsMap.get(sectionId)!.push(permission);
  });
  
  // Convertir el mapa a array de secciones ordenadas
  const sections: PermissionSection[] = [];
  
  // Ordenar las secciones según el orden deseado
  const sectionOrder = [
    PERMISSION_SECTIONS.ROLES_AND_PERMISSIONS,
    PERMISSION_SECTIONS.ORDERS_AND_QUOTATIONS,
    PERMISSION_SECTIONS.ENVIOS,
    PERMISSION_SECTIONS.BODEGA,
    PERMISSION_SECTIONS.PRODUCTS,
    PERMISSION_SECTIONS.OTHERS
  ];
  
  sectionOrder.forEach(sectionId => {
    if (sectionsMap.has(sectionId)) {
      sections.push({
        id: sectionId,
        name: SECTION_NAMES[sectionId as keyof typeof SECTION_NAMES] || 'Sección Desconocida',
        permissions: sectionsMap.get(sectionId)!.sort((a, b) => a.name.localeCompare(b.name))
      });
    }
  });
  
  // Agregar cualquier sección que no esté en el orden predefinido
  sectionsMap.forEach((permissions, sectionId) => {
    if (!sectionOrder.includes(sectionId as any)) {
      sections.push({
        id: sectionId,
        name: `Sección ${sectionId}`,
        permissions: permissions.sort((a, b) => a.name.localeCompare(b.name))
      });
    }
  });
  
  return sections;
};
